import React, { useState } from 'react';

import { ParsedData, StepProps } from '@betterment/js-runtime';
import { Box } from '@betterment/samba-react';
import { Button, CenteredWorkflowLayout, ErrorText, Text } from '@betterment/style-closet-react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { AddressFormValues, RequestValues } from './Controller';

import { AddressForm, AddressSchema, buildResidentialAddressValidation } from '../Checking/address-helpers';
import NotFound from '../Checking/components/NotFound';
import {
  Address,
  AddressParams,
  AddressValidationParams,
  ProspectJointInvitationRequestParams,
  ProspectJointInvitationRequestResponse,
} from '../Checking/data';
import Footer from '../Shared/components/Footer';

const ConfirmFormSchema = Yup.object<Partial<AddressParams>>().shape({
  residentialAddress: Yup.object().shape(AddressSchema),
});

export interface Props extends StepProps<RequestValues> {
  validateAddress: (data?: AddressValidationParams) => Promise<ParsedData<{}>>;
  createProspectJointInvitationRequest: (
    data?: ProspectJointInvitationRequestParams
  ) => Promise<ParsedData<ProspectJointInvitationRequestResponse>>;
}

const JointAccountInvitationProspectSecondaryAddress: React.FC<React.PropsWithChildren<Props>> = ({
  goToNextStep,
  flowState,
  validateAddress,
  createProspectJointInvitationRequest,
}) => {
  const [isEditingAddress, setIsEditingAddress] = useState(false);

  const { email, socialSecurityNumber, firstName, middleName, lastName, phoneNumber, dateOfBirth, residentialAddress } =
    flowState.values;

  if (!firstName || !lastName || !email || !socialSecurityNumber || !phoneNumber || !dateOfBirth) {
    return <NotFound />;
  }

  const defaultInitialValue: Address = {
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
  };

  const validationSchema = ConfirmFormSchema.test(
    'is-remote-valid',
    'address is invalid',
    buildResidentialAddressValidation(validateAddress)
  );

  const onSubmit = async (values: AddressFormValues) => {
    const requestParams: ProspectJointInvitationRequestParams = {
      email,
      socialSecurityNumber,
      firstName,
      middleName,
      lastName,
      dateOfBirth,
      phoneNumber,
      residentialAddress: values.residentialAddress,
    };
    const { data, errors } = await createProspectJointInvitationRequest(requestParams);

    if (errors) {
      goToNextStep({ residentialAddress: values.residentialAddress });
    } else if (data && !data.isEligible) {
      goToNextStep({
        eligibilityError: { title: data.ineligibilityMessageTitle, message: data.ineligibilityMessageDescription },
      });
    } else if (data?.isEligible) {
      goToNextStep({ requestId: data.requestId, residentialAddress: values.residentialAddress });
    }
  };

  const initialAddressValue: AddressFormValues = {
    residentialAddress: residentialAddress || defaultInitialValue,
  };

  return (
    <CenteredWorkflowLayout title="Joint Checking signup" heading={`What is ${firstName}'s residential address?`}>
      <CenteredWorkflowLayout.Slot name="content">
        <Text as="dd" m="0" pb="8">
          Confirm that the address below is correct.
        </Text>
        <Formik
          onSubmit={onSubmit}
          validationSchema={validationSchema}
          initialValues={initialAddressValue}
          validateOnBlur={false}
          validateOnChange={false}
        >
          {({ status, values }) => (
            <Form>
              <Box
                css={{
                  marginBottom: 'space.400',
                }}
              >
                <AddressForm
                  titleText="Residential address"
                  fieldNamePrefix="residentialAddress"
                  onEdit={() => setIsEditingAddress(true)}
                >
                  <AddressForm.FormattedAddress {...values.residentialAddress} />
                </AddressForm>
              </Box>
              <CenteredWorkflowLayout.Actions>
                <Button type="submit">{isEditingAddress ? 'Save and continue' : 'Continue'}</Button>
              </CenteredWorkflowLayout.Actions>
              {status && <ErrorText>{status}</ErrorText>}
            </Form>
          )}
        </Formik>
      </CenteredWorkflowLayout.Slot>
      <CenteredWorkflowLayout.Slot name="footer">
        <Footer />
      </CenteredWorkflowLayout.Slot>
    </CenteredWorkflowLayout>
  );
};

export default JointAccountInvitationProspectSecondaryAddress;
